/**
 * Resource Catalog Tab
 * Manages ResourceCatalog entries with inline editing using TanStack Table v8
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Chip,
  Stack,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Menu,
  Icon,
} from '@mui/material';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from '@tanstack/react-table';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import AdminButton from 'components/common/AdminButton';
import { useResourceCatalog, useResourceTypes } from '../hooks';
import { getNextResourceCode, validateResourceCode } from '../services/resourceService';

const DOMAINS = ['OPERATIONS', 'FINANCE', 'HUMAN_RESOURCES', 'INFORMATION_TECHNOLOGY', 'MARKETING', 'EXECUTIVE'];
const ACTIONS = ['VIEW', 'EDIT', 'CREATE', 'DELETE', 'DEACTIVATE', 'EXPORT'];

/**
 * Resource Catalog Tab Component
 * Displays and manages resource catalog entries in an editable table
 */
function ResourceCatalogTab() {
  const { catalogEntries, isLoading, error, create, update, remove, isCreating, isUpdating, isDeleting } = useResourceCatalog();
  const { resourceTypes } = useResourceTypes();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [formData, setFormData] = useState({
    resourceCode: '',
    resourceTypeId: '',
    domain: 'OPERATIONS',
    name: '',
    route: '',
    componentPath: '',
    description: '',
    icon: '',
    defaultActions: ['VIEW'],
    status: 'ACTIVE',
    version: 1,
  });
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  // Resource code auto-generation state
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [codeValidationError, setCodeValidationError] = useState('');
  const [codeHelperText, setCodeHelperText] = useState('');
  const [wasManuallyEdited, setWasManuallyEdited] = useState(false);

  // Handle ResourceType change with auto-generation
  const handleResourceTypeChange = async (newTypeId) => {
    setFormData({ ...formData, resourceTypeId: newTypeId });

    // Only auto-generate in create mode
    if (newTypeId && modalMode === 'create') {
      setIsGeneratingCode(true);
      setCodeValidationError('');
      setCodeHelperText('');
      setWasManuallyEdited(false);

      try {
        const nextCode = await getNextResourceCode(newTypeId);
        setFormData(prev => ({ ...prev, resourceCode: nextCode }));
        setCodeHelperText(`Next available: ${nextCode}`);
      } catch (err) {
        console.error('Failed to generate resource code:', err);
        setCodeValidationError(err.message || 'Failed to generate code');
      } finally {
        setIsGeneratingCode(false);
      }
    }
  };

  // Handle manual resourceCode change with validation
  const handleResourceCodeChange = async (newCode) => {
    const upperCode = newCode.toUpperCase();
    setFormData({ ...formData, resourceCode: upperCode });
    setWasManuallyEdited(true);

    if (upperCode && formData.resourceTypeId) {
      setCodeValidationError('');
      setCodeHelperText('Validating...');

      try {
        const validation = await validateResourceCode(
          upperCode,
          formData.resourceTypeId,
          modalMode === 'edit' ? formData.id : null
        );

        if (!validation.valid) {
          setCodeValidationError(validation.error);
          setCodeHelperText('');
        } else {
          setCodeValidationError('');
          setCodeHelperText('Manually edited');
        }
      } catch (err) {
        console.error('Failed to validate resource code:', err);
        setCodeValidationError(err.message || 'Validation failed');
        setCodeHelperText('');
      }
    }
  };

  // Check if form is valid for saving
  const isFormValid = () => {
    // Check required fields
    if (!formData.resourceCode || !formData.resourceTypeId || !formData.domain ||
        !formData.name || !formData.defaultActions || formData.defaultActions.length === 0) {
      return false;
    }

    // Check for validation errors
    if (codeValidationError) {
      return false;
    }

    // Check if code is being generated
    if (isGeneratingCode) {
      return false;
    }

    return true;
  };

  // Open modal for creating new entry
  const handleOpenCreate = () => {
    setModalMode('create');
    setFormData({
      resourceCode: '',
      resourceTypeId: '',
      domain: 'OPERATIONS',
      name: '',
      route: '',
      componentPath: '',
      description: '',
      icon: '',
      defaultActions: ['VIEW'],
      status: 'ACTIVE',
      version: 1,
    });
    setIsGeneratingCode(false);
    setCodeValidationError('');
    setCodeHelperText('');
    setWasManuallyEdited(false);
    setModalOpen(true);
  };

  // Open modal for editing existing entry
  const handleOpenEdit = (row) => {
    setModalMode('edit');
    setFormData({ ...row });
    setModalOpen(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setFormData({
      resourceCode: '',
      resourceTypeId: '',
      domain: 'OPERATIONS',
      name: '',
      route: '',
      componentPath: '',
      description: '',
      icon: '',
      defaultActions: ['VIEW'],
      status: 'ACTIVE',
      version: 1,
    });
    setIsGeneratingCode(false);
    setCodeValidationError('');
    setCodeHelperText('');
    setWasManuallyEdited(false);
  };

  // Save modal changes
  const handleSaveModal = async () => {
    if (!formData.resourceCode || !formData.resourceTypeId || !formData.domain || !formData.name || formData.defaultActions.length === 0) {
      alert('Resource Code, Resource Type, Domain, Name, and Default Actions are required');
      return;
    }

    // Check for validation errors
    if (codeValidationError) {
      alert(`Cannot save: ${codeValidationError}`);
      return;
    }

    try {
      if (modalMode === 'create') {
        await create(formData);
      } else {
        // Filter out read-only fields that shouldn't be in the update mutation
        const { __typename, createdAt, updatedAt, ...updateData } = formData;
        await update(updateData);
      }
      handleCloseModal();
    } catch (err) {
      console.error('Failed to save resource catalog:', err);
      alert(err.message || 'Failed to save resource catalog');
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this resource catalog entry?')) {
      try {
        await remove(id);
      } catch (err) {
        console.error('Failed to delete resource catalog:', err);
      }
    }
  };

  // Handle menu open
  const handleMenuOpen = (event, row) => {
    setMenuAnchor(event.currentTarget);
    setSelectedRow(row);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedRow(null);
  };

  // Handle menu edit
  const handleMenuEdit = () => {
    if (selectedRow) {
      handleOpenEdit(selectedRow);
      handleMenuClose();
    }
  };

  // Handle menu delete
  const handleMenuDelete = () => {
    if (selectedRow) {
      handleDelete(selectedRow.id);
      handleMenuClose();
    }
  };

  // Table columns
  const columns = useMemo(() => [
    {
      accessorKey: 'resource',
      header: 'Resource',
      cell: ({ row }) => (
        <Box display="flex" alignItems="center" gap={1.5}>
          {row.original.icon && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '32px',
                height: '32px',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                color: '#7C3AED',
              }}
            >
              <Icon fontSize="small">{row.original.icon}</Icon>
            </Box>
          )}
          <Box>
            <Typography variant="body2" fontWeight="bold">
              {row.original.resourceCode}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {row.original.name}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      accessorKey: 'typeGroup',
      header: 'Type & Group',
      cell: ({ row }) => {
        const resourceType = resourceTypes.find((rt) => rt.id === row.original.resourceTypeId);
        return (
          <Box>
            <Typography variant="body2" fontWeight="bold">
              {resourceType ? resourceType.name : '—'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {row.original.domain}
            </Typography>
          </Box>
        );
      },
    },
    {
      accessorKey: 'routeComponent',
      header: 'Route & Component',
      cell: ({ row }) => (
        <Box>
          <Typography variant="body2" fontWeight="bold">
            {row.original.route || '—'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {row.original.componentPath || '—'}
          </Typography>
        </Box>
      ),
    },
    {
      accessorKey: 'defaultActions',
      header: 'Available Actions',
      cell: ({ row }) => {
        const actions = row.original.defaultActions || [];
        return (
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {actions.map((action) => (
              <Chip key={action} label={action} size="small" color="primary" variant="outlined" />
            ))}
          </Stack>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Box display="flex" justifyContent="center">
          <Chip
            label={row.original.status}
            color={row.original.status === 'ACTIVE' ? 'success' : 'default'}
            size="small"
            variant="outlined"
          />
        </Box>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <Box display="flex" justifyContent="center">
          <IconButton
            size="small"
            onClick={(e) => handleMenuOpen(e, row.original)}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ], [resourceTypes]);

  // Table instance
  const table = useReactTable({
    data: catalogEntries,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Failed to load resource catalog: {error.message}
      </Alert>
    );
  }

  return (
    <Box>
      {/* White Background Container */}
      <Box sx={{
        backgroundColor: '#fff',
        borderRadius: 2,
        p: 3,
      }}>
        {/* Header Section */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Resource Catalog
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View and manage all resources in the system.
            </Typography>
          </Box>
          <AdminButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
          >
            Add Resource
          </AdminButton>
        </Box>

        {/* Table */}
        <Box sx={{
          overflowX: 'auto',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
        }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
          }}>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        borderBottom: '2px solid #e0e0e0',
                        backgroundColor: '#f5f5f5',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                      }}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {/* Data rows */}
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={{
                        padding: '12px 16px',
                        borderBottom: '1px solid #e0e0e0',
                        backgroundColor: '#fff',
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}

              {/* Empty state */}
              {table.getRowModel().rows.length === 0 && (
                <tr>
                  <td
                    colSpan={columns.length}
                    style={{
                      padding: '48px 16px',
                      textAlign: 'center',
                      color: '#999',
                      backgroundColor: '#fff',
                    }}
                  >
                    No resources found. Click "Add Resource" to create one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Box>
      </Box>

      {/* Create/Edit Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {modalMode === 'create' ? 'Create New Resource' : 'Edit Resource'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Resource Type - MOVED TO FIRST POSITION */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Resource Type</InputLabel>
                <Select
                  value={formData.resourceTypeId}
                  onChange={(e) => handleResourceTypeChange(e.target.value)}
                  label="Resource Type"
                  disabled={modalMode === 'edit'}
                >
                  {resourceTypes.map((rt) => (
                    <MenuItem key={rt.id} value={rt.id}>
                      {rt.name} ({rt.resourcePrefix})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Resource Code - NOW SECOND POSITION WITH AUTO-GENERATION */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Resource Code"
                value={formData.resourceCode}
                onChange={(e) => handleResourceCodeChange(e.target.value)}
                fullWidth
                required
                disabled={modalMode === 'edit' || isGeneratingCode}
                error={Boolean(codeValidationError)}
                helperText={
                  codeValidationError ||
                  (modalMode === 'edit' ? 'Cannot change code' : codeHelperText || 'Select Resource Type to auto-generate')
                }
                InputProps={{
                  endAdornment: isGeneratingCode && (
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                  ),
                }}
              />
            </Grid>

            {/* Name */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                fullWidth
                required
              />
            </Grid>

            {/* Route */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Route"
                value={formData.route}
                onChange={(e) => setFormData({ ...formData, route: e.target.value })}
                fullWidth
                placeholder="/path/to/page"
                helperText="Frontend route path (e.g., /admin/resource-admin)"
              />
            </Grid>

            {/* Component Path */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Component Path"
                value={formData.componentPath}
                onChange={(e) => setFormData({ ...formData, componentPath: e.target.value })}
                fullWidth
                placeholder="ComponentName"
                helperText="Component identifier (e.g., ResourceAdmin)"
              />
            </Grid>

            {/* Domain */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Domain</InputLabel>
                <Select
                  value={formData.domain}
                  onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                  label="Domain"
                >
                  {DOMAINS.map((domain) => (
                    <MenuItem key={domain} value={domain}>
                      {domain}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                label="Description"
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>

            {/* Icon */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Icon"
                value={formData.icon || ''}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                fullWidth
                helperText="Material icon name (e.g., admin_panel_settings)"
              />
            </Grid>

            {/* Status */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  label="Status"
                >
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Default Actions */}
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Default Actions</InputLabel>
                <Select
                  multiple
                  value={formData.defaultActions}
                  onChange={(e) => setFormData({ ...formData, defaultActions: e.target.value })}
                  label="Default Actions"
                  renderValue={(selected) => (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {selected.map((action) => (
                        <Chip key={action} label={action} size="small" />
                      ))}
                    </Stack>
                  )}
                >
                  {ACTIONS.map((action) => (
                    <MenuItem key={action} value={action}>
                      {action}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <AdminButton onClick={handleCloseModal} variant="outlined">
            Cancel
          </AdminButton>
          <AdminButton
            onClick={handleSaveModal}
            variant="contained"
            disabled={!isFormValid() || isCreating || isUpdating}
          >
            {isCreating || isUpdating ? 'Saving...' : 'Save'}
          </AdminButton>
        </DialogActions>
      </Dialog>

      {/* Row Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleMenuEdit}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleMenuDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
}

export default ResourceCatalogTab;
